import 'bootstrap/dist/css/bootstrap.min.css';
import './fonts/PixelifySans-Regular.ttf';

import { useEffect, useState } from "react";
import { callAPI } from "./callAPI";
import { Button, Container, Form, InputGroup, Modal, Navbar } from "react-bootstrap";
import { collection, addDoc, getDocs, query, orderBy, deleteDoc } from "firebase/firestore";
import ReactMarkdown from 'react-markdown';
import jsPDF from "jspdf";


import { auth, db } from "./firebase.js";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from "firebase/auth";

import { Eye, EyeOff } from "lucide-react";
import Nav from 'react-bootstrap/Nav';
import './App.css';

function App() {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [callSign, setCallSign] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFont, setSelectedFont] = useState('Pixel');
  const [dashboardData, setDashboardData] = useState([]);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false); // lets user view their password
  const [isPWConfirmationVisible, setIsPWConfirmationVisible] = useState(false); // lets user view their password confirmation
  const [activeNav, setActiveNav] = useState("link-1"); // used to highlight the current user view (home/dashboard/settings)

  const fontOptions = ['Pixel', 'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana'];

  useEffect(() => {
    document.body.style.fontFamily = selectedFont;
    localStorage.setItem("userFont", selectedFont);
  }, [selectedFont]);

  useEffect(() => {
    const storedFont = localStorage.getItem("userFont");
    if (storedFont) setSelectedFont(storedFont);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userHistoryRef = collection(db, "users", currentUser.uid, "history");
        const qSnap = await getDocs(query(userHistoryRef, orderBy("timestamp", "desc")));

        const displayName = currentUser.email.split('@')[0];

        if (!qSnap.empty) {
          const lastEntry = qSnap.docs[0].data();
          const recentTopic = lastEntry.question;
          const greeting = `Welcome back, ${displayName}. You’ve been reviewing "${recentTopic}". Let’s continue from where we left off.`;
          setMessages([{ type: 'ai', text: greeting }]);
        } else {
          const welcomeMessage = `Welcome aboard, ${displayName}. How can we start our training?`;
          setMessages([{ type: 'ai', text: welcomeMessage }]);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setShowLogin(true);
  }, []);

  const handleCloseLogin = () => setShowLogin(false);
  const handleShowLogin = () => setShowLogin(true);
  const handleCloseSettings = () => {
    setActiveNav("link-1");
    setShowSettings(false);
  };
  const handleShowSettings = () => {
    setActiveNav('link-3');
    setShowSettings(true);
  };
  const handleCloseDashboard = () =>  {
    setActiveNav("link-1");
    setShowDashboard(false);
  }

  const handleUserInput = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    const newMessages = [...messages, { type: 'user', text: userMessage }];
    setMessages(newMessages);

    try {
      const reply = await callAPI(userMessage);
      setMessages([...newMessages, { type: 'ai', text: reply }]);

      if (user) {
        const userHistoryRef = collection(db, "users", user.uid, "history");
        await addDoc(userHistoryRef, {
          question: userMessage,
          answer: reply,
          timestamp: new Date()
        });

        const qSnap = await getDocs(query(userHistoryRef, orderBy("timestamp")));
        if (qSnap.size > 3) {
          const excess = qSnap.docs.slice(0, qSnap.size - 3);
          for (const docSnap of excess) await deleteDoc(docSnap.ref);
        }
      }
    } catch (err) {
      console.error("API or Firestore error", err);
      setMessages([...newMessages, { type: 'ai', text: "Something went wrong." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    const email = callSign.trim().toLowerCase() + "@ltfalcon.com";  
    const pw = password.trim();
    const pwConfirm = passwordConfirm.trim();

    if (pwConfirm !== pw) return alert("Passwords are not matching.");
    if (pw.length <= 5) return alert("Password must be atleast 6 characters.")
    if (!callSign || !pw) return alert("Call Sign and Password are required.");

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, pw);
      console.log("Signed in:", userCredential.user);
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        const newUser = await createUserWithEmailAndPassword(auth, email, pw);
        console.log("Created new user:", newUser.user);
      } else if (error.code === 'auth/wrong-password') {
        alert("Incorrect password.");
      } else {
        console.error("Authentication error:", error);
        alert("Authentication error: " + error.message);
      }
    }
    handleCloseLogin();
  };

  const handleSignOut = async () => {
    await signOut(auth);
    console.log("User signed out");
  };

  const handleDashboard = async (e) => {
    e.preventDefault();

    if (!user) return;

    const userHistoryRef = collection(db, "users", user.uid, "history");
    const qSnap = await getDocs(query(userHistoryRef, orderBy("timestamp", "desc")));

    const data = qSnap.docs.map(doc => doc.data()).slice(0, 3); // get latest 3
    setActiveNav("link-2");
    setDashboardData(data);
    setShowDashboard(true);
  }

  const exportDashboardToPDF = () => {
    if (!user || dashboardData.length === 0) return;

    const doc = new jsPDF();
    const margin = 10;
    const maxWidth = 180;
    const lineHeight = 7;
    let y = 20;

    const callSignText = `Call Sign: ${user.email.split('@')[0]}`;
    doc.setFontSize(16);
    doc.text(callSignText, margin, y);
    y += 10;
    doc.text("Mission Debrief:", margin, y);
    y += 10;

    doc.setFontSize(12);

    dashboardData.forEach((entry, index) => {
      const question = `Q${index + 1}: ${entry.question}`;
      const answer = `A${index + 1}: ${entry.answer}`;

      const questionLines = doc.splitTextToSize(question, maxWidth);
      const answerLines = doc.splitTextToSize(answer, maxWidth);
      const totalLines = questionLines.length + answerLines.length;

      // Check if content fits, else add new page
      if (y + totalLines * lineHeight > 280) {
        doc.addPage();
        y = 20;
      }

      doc.text(questionLines, margin, y);
      y += questionLines.length * lineHeight;

      doc.text(answerLines, margin, y);
      y += answerLines.length * lineHeight + 5;
    });

    // Add date to filename
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const filename = `Mission_Debrief_${yyyy}-${mm}-${dd}.pdf`;

    doc.save(filename);
  };




  return (
      <div className="App" style={{ fontFamily: selectedFont }}>
        <div className="cockpit">
          <div className="hud">
            <Navbar expand="lg" data-bs-theme="dark">
              <Container fluid>
                <Navbar.Toggle aria-controls="main-navbar" />
                <Navbar.Collapse id="main-navbar" className="justify-content-between align-items-center">
                  <Nav variant="pills" activeKey={activeNav}>
                    <Nav.Item><Nav.Link eventKey="link-1">Home</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="link-2" onClick={handleDashboard}>Dashboard</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="link-3" onClick={handleShowSettings}>Settings</Nav.Link></Nav.Item>
                  </Nav>
                  <div className="ms-auto d-flex align-items-center gap-2">
                    {user && <span id="username">Call Sign: {user.email.split('@')[0]}</span>}
                    <Button id="auth-button" onClick={user ? handleSignOut : handleShowLogin} className="login">
                      {user ? "Sign Out" : "Sign In"}
                    </Button>
                  </div>
                </Navbar.Collapse>
              </Container>
            </Navbar>

            <div className="ai-panel">
              <h2>AI CO-PILOT</h2>
              <div id="chatBox" className="chat-box">
                {messages.map((msg, i) => (
                    <div key={i} className={`chat-message ${msg.type}`}>
                      <strong>{msg.type === 'user' ? 'You' : 'AI'}:</strong>
                      <div className="chat-text">
                        {msg.type === 'ai' ? (
                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                        ) : (
                            <p>{msg.text}</p>
                        )}
                      </div>
                    </div>
                ))}
                {loading && <div className="chat-message ai"><strong>AI:</strong> Loading...</div>}
              </div>
              <form onSubmit={handleUserInput} className="chat-input-form">
                <input type="text" placeholder="Type here" className="input" value={input} onChange={(e) => setInput(e.target.value)} />
                <button type="submit" id="user-submit">Submit</button>
              </form>

              <div className="status-box">
                <h3>SYSTEM STATUS</h3>
                <div className="status-row">
                  <div className="status-item"><strong>FUEL:</strong> <span className="status-ok">WITHIN LIMITS</span></div>
                  <div className="status-item"><strong>OIL PRESSURE:</strong> <span className="status-ok">NORMAL</span></div>
                </div>
                <div className="status-row">
                  <div className="status-item"><strong>HYDRAULICS:</strong> <span className="status-ok">GOOD</span></div>
                  <div className="status-item"><strong>BATTERY:</strong> <span className="status-ok">CHARGED</span></div>
                </div>
              </div>
            </div>

            <div className="metrics">
              <div className="metric"><span className="label">SPEED</span><span className="value">585</span></div>
              <div className="metric"><span className="label">ALTITUDE</span><span className="value">10,200</span></div>
            </div>
          </div>
        </div>

        <Modal show={showLogin} onHide={handleCloseLogin} backdrop="static">
          <Modal.Header><Modal.Title>Welcome to LT. Falcon</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSignIn} id="loginForm">
              <Form.Group className="mb-3"><Form.Label>Call Sign</Form.Label>
                <Form.Control type="text" placeholder="Call Sign" value={callSign} onChange={e => setCallSign(e.target.value)} autoFocus /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Name</Form.Label>
                <Form.Control type="text" placeholder="Name" value={name} onChange={e => setName(e.target.value)} /></Form.Group>
              <Form.Label>Password</Form.Label>
              <Form.Group className="mb-3">
                <InputGroup className="mb-3">
                <Form.Control className="border-end-0" type={isPasswordVisible ? "text" : "password"} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
                <InputGroup.Text
                as="button"
                type="button"
                className="bg-white border-0 rounded-start-0"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
                  {isPasswordVisible ? <Eye color='gray'/> : <EyeOff color='gray'/>}
                </InputGroup.Text>
                </InputGroup>
                </Form.Group>
                <Form.Label>Confirm Password</Form.Label>
              <Form.Group className="mb-3">
                <InputGroup className="mb-3">
              <Form.Control className="border-end-0" type={isPWConfirmationVisible ? "text" : "password"} placeholder='Password' value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)}/>
                <InputGroup.Text
                  as="button"
                  type="button"
                  className="bg-white border-0 rounded-start-0 "
                  onClick={() => setIsPWConfirmationVisible(!isPWConfirmationVisible)}
                  >
                {isPWConfirmationVisible ? <Eye color='gray'/> : <EyeOff color='gray'/>}
              </InputGroup.Text>
              </InputGroup>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseLogin}>Close</Button>
            <Button variant="primary" type="submit" form="loginForm">Save Changes</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showSettings} onHide={handleCloseSettings} backdrop="static">
          <Modal.Header><Modal.Title>Settings</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Select aria-label="Select Font" value={selectedFont} onChange={(e) => setSelectedFont(e.target.value)} className="mb-3">
              {fontOptions.map((font) => (<option key={font} value={font}>{font}</option>))}
            </Form.Select>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseSettings}>Close</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showDashboard} onHide={handleCloseDashboard} backdrop="static" fullscreen id="dashboardModal">
          <Modal.Header><Modal.Title>Dashboard</Modal.Title></Modal.Header>
          <Modal.Body>
            <h5>Recent Questions & Answers</h5>
            {dashboardData.length === 0 ? (
                <p>No recent history.</p>
            ) : (
                dashboardData.map((entry, index) => (
                    <div key={index} className="mb-3">
                      <strong>Q{index + 1}:</strong> {entry.question}<br />
                      <strong>A{index + 1}:</strong> <ReactMarkdown>{entry.answer}</ReactMarkdown>
                      <hr />
                    </div>
                ))
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDashboard}>Close</Button>
            <Button variant="primary" onClick={exportDashboardToPDF}>Export to PDF</Button>
          </Modal.Footer>
        </Modal>
      </div>
  );
}

export default App;

